import React, { useMemo } from "react";
import Dropdown from "../../common/dropDown";
import {
  dateInputBounds,
  evaluateCalculatedValue,
  evaluateLogicVisibility,
  fieldWidthClass,
  isDigitsOnlyPattern,
  isLayoutField,
} from "./esourceFieldTypes";
import ESourceSignatureCapture from "./ESourceSignatureCapture";

function toDropdownOptions(rawOptions) {
  return (rawOptions || []).map((opt) => ({
    value: String(opt),
    label: String(opt),
  }));
}

function emptyRow(columns) {
  const row = {};
  (columns || []).forEach((c) => {
    row[c.fieldName] = "";
  });
  return row;
}

/**
 * Shared control renderer for builder preview, instance runtime, and public fill.
 */
export function ESourceFieldControl({
  field,
  value,
  disabled = false,
  onChange,
  idPrefix = "",
  allFields = [],
  allValues = {},
}) {
  const inputId = `${idPrefix}${field.id}`;
  const setVal = (v) => onChange?.(field.id, v);

  const choiceOptions = useMemo(
    () => toDropdownOptions(field.options),
    [field.options],
  );

  if (field.type === "section") {
    return (
      <div className="esource-form-section">
        <h6 className="esource-form-section__title mb-0">{field.label}</h6>
        {field.helpText ? (
          <p className="esource-form-section__hint mb-0">{field.helpText}</p>
        ) : null}
      </div>
    );
  }

  if (field.type === "heading") {
    return (
      <div className="esource-form-heading">
        <h5 className="esource-form-heading__title mb-0">{field.label}</h5>
      </div>
    );
  }

  if (field.type === "instruction") {
    return (
      <div className="esource-form-instruction">
        <i className="fas fa-info-circle me-1" aria-hidden="true" />
        <span>{field.label}</span>
      </div>
    );
  }

  if (field.type === "divider") {
    return <hr className="esource-form-divider" />;
  }

  if (field.type === "spacer") {
    return <div className="esource-form-spacer" aria-hidden="true" />;
  }

  if (field.type === "pagebreak") {
    return (
      <div className="esource-form-pagebreak">
        <span>Page break</span>
      </div>
    );
  }

  if (field.type === "calculated") {
    const computed = evaluateCalculatedValue(field, allFields, allValues);
    const display =
      computed === "" || computed == null
        ? "—"
        : typeof computed === "number"
          ? Number.isInteger(computed)
            ? String(computed)
            : String(Math.round(computed * 1000) / 1000)
          : String(computed);
    return (
      <input
        id={inputId}
        className="form-control"
        type="text"
        readOnly
        disabled
        value={display}
        aria-label={field.label || "Calculated value"}
      />
    );
  }

  if (field.type === "repeating_table") {
    const columns = Array.isArray(field.columns) ? field.columns : [];
    const rows = Array.isArray(value) ? value : [];
    const maxRows = Number(field.maxRows) || 50;
    const minRows = Number(field.minRows) || 0;

    const setRows = (next) => setVal(next);

    const updateCell = (rowIndex, fieldName, cellValue) => {
      const next = rows.map((r, i) =>
        i === rowIndex ? { ...r, [fieldName]: cellValue } : r,
      );
      setRows(next);
    };

    const addRow = () => {
      if (rows.length >= maxRows) return;
      setRows([...rows, emptyRow(columns)]);
    };

    const removeRow = (rowIndex) => {
      if (rows.length <= minRows) return;
      setRows(rows.filter((_, i) => i !== rowIndex));
    };

    return (
      <div id={inputId} className="esource-form-rt">
        <div className="table-responsive">
          <table className="table table-sm table-bordered esource-form-rt__table mb-2">
            <thead>
              <tr>
                {columns.map((col) => (
                  <th key={col.id}>{col.label}</th>
                ))}
                <th className="esource-form-rt__actions-col" />
              </tr>
            </thead>
            <tbody>
              {rows.map((row, ri) => (
                <tr key={`row-${ri}`}>
                  {columns.map((col) => {
                    const cellId = `${inputId}_${ri}_${col.fieldName}`;
                    const cellVal = row?.[col.fieldName] ?? "";
                    if (col.type === "dropdown") {
                      const opts = toDropdownOptions(col.options);
                      const clearOpt = { value: "", label: "Select..." };
                      const selected =
                        cellVal !== ""
                          ? opts.find((o) => o.value === String(cellVal)) || {
                              value: String(cellVal),
                              label: String(cellVal),
                            }
                          : clearOpt;
                      return (
                        <td key={col.id}>
                          <Dropdown
                            options={[clearOpt, ...opts]}
                            isMulti={false}
                            showSearch
                            disabled={disabled}
                            selected={selected}
                            onSelect={(opt) =>
                              updateCell(ri, col.fieldName, opt?.value ?? "")
                            }
                          />
                        </td>
                      );
                    }
                    const inputType =
                      col.type === "integer" || col.type === "number"
                        ? "number"
                        : col.type === "date"
                          ? "date"
                          : "text";
                    return (
                      <td key={col.id}>
                        <input
                          id={cellId}
                          className="form-control form-control-sm"
                          type={inputType}
                          disabled={disabled}
                          value={cellVal}
                          step={col.type === "integer" ? "1" : undefined}
                          onChange={(e) =>
                            updateCell(ri, col.fieldName, e.target.value)
                          }
                        />
                      </td>
                    );
                  })}
                  <td>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger"
                      disabled={disabled || rows.length <= minRows}
                      title="Remove row"
                      onClick={() => removeRow(ri)}
                    >
                      <i className="fas fa-trash-alt" aria-hidden="true" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <button
          type="button"
          className="btn btn-sm btn-outline-primary"
          disabled={disabled || rows.length >= maxRows}
          onClick={addRow}
        >
          <i className="fas fa-plus me-1" aria-hidden="true" />
          {field.addRowLabel || "Add row"}
        </button>
      </div>
    );
  }

  if (field.type === "textarea") {
    const digitsOnly = isDigitsOnlyPattern(field.pattern);
    return (
      <textarea
        id={inputId}
        className="form-control"
        rows={3}
        disabled={disabled}
        value={value ?? ""}
        placeholder={field.placeholder || undefined}
        maxLength={field.maxLength ? Number(field.maxLength) : undefined}
        onChange={(e) => {
          let next = e.target.value;
          if (digitsOnly) {
            next = next.replace(/\D/g, "");
            const max = field.maxLength ? Number(field.maxLength) : null;
            if (Number.isFinite(max) && max > 0) next = next.slice(0, max);
          }
          setVal(next);
        }}
      />
    );
  }

  if (field.type === "dropdown") {
    const placeholder =
      String(field.placeholder || "").trim() || "Select...";
    const clearOpt = { value: "", label: placeholder };
    const options = [clearOpt, ...choiceOptions];
    const selected =
      value != null && String(value) !== ""
        ? choiceOptions.find((o) => o.value === String(value)) || {
            value: String(value),
            label: String(value),
          }
        : clearOpt;
    return (
      <div id={inputId} className="esource-form-dd">
        <Dropdown
          options={options}
          isMulti={false}
          showSearch
          disabled={disabled}
          selected={selected}
          onSelect={(opt) => setVal(opt?.value ?? "")}
        />
      </div>
    );
  }

  if (field.type === "radio" || field.type === "yesno") {
    const options =
      field.options?.length > 0
        ? field.options
        : field.type === "yesno"
          ? ["Yes", "No"]
          : [];
    const stacked = field.optionsLayout !== "inline";
    return (
      <div
        className={`esource-form-choice${
          stacked
            ? " esource-form-choice--stacked"
            : " esource-form-choice--inline"
        }`}
        role="radiogroup"
        aria-labelledby={`${inputId}-label`}
      >
        {options.map((opt) => {
          const optId = `${inputId}_${String(opt).replace(/\s+/g, "_")}`;
          return (
            <div
              className={`form-check${stacked ? "" : " form-check-inline"}`}
              key={opt}
            >
              <input
                className="form-check-input"
                type="radio"
                name={inputId}
                id={optId}
                disabled={disabled}
                checked={String(value ?? "") === String(opt)}
                onChange={() => setVal(opt)}
              />
              <label className="form-check-label" htmlFor={optId}>
                {opt}
              </label>
            </div>
          );
        })}
      </div>
    );
  }

  if (field.type === "toggle") {
    const options =
      field.options?.length >= 2 ? field.options : ["Yes", "No"];
    const onVal = String(options[0]);
    const offVal = String(options[1]);
    let checked = false;
    if (typeof value === "boolean") {
      checked = value;
    } else if (value != null && String(value).trim() !== "") {
      checked = String(value) === onVal;
    }
    return (
      <div className="esource-form-toggle">
        <div className="form-check form-switch mb-0">
          <input
            className="form-check-input"
            type="checkbox"
            role="switch"
            id={inputId}
            disabled={disabled}
            checked={checked}
            onChange={(e) => setVal(e.target.checked ? onVal : offVal)}
          />
          <label className="form-check-label" htmlFor={inputId}>
            {checked ? onVal : offVal}
          </label>
        </div>
      </div>
    );
  }

  if (field.type === "button_group") {
    const options =
      field.options?.length > 0
        ? field.options
        : ["Option 1", "Option 2", "Option 3"];
    return (
      <div
        className="esource-form-btn-group"
        role="group"
        aria-labelledby={`${inputId}-label`}
      >
        {options.map((opt) => {
          const selected = String(value ?? "") === String(opt);
          return (
            <button
              key={opt}
              type="button"
              className={`esource-form-btn-group__btn${
                selected ? " is-selected" : ""
              }`}
              disabled={disabled}
              aria-pressed={selected}
              onClick={() => setVal(opt)}
            >
              {opt}
            </button>
          );
        })}
      </div>
    );
  }

  if (field.type === "checkbox") {
    return (
      <div className="form-check">
        <input
          className="form-check-input"
          type="checkbox"
          id={inputId}
          disabled={disabled}
          checked={!!value}
          onChange={(e) => setVal(e.target.checked)}
        />
        <label className="form-check-label" htmlFor={inputId}>
          {field.label}
          {field.required ? <span className="mandatory"> *</span> : null}
        </label>
      </div>
    );
  }

  if (field.type === "signature") {
    return (
      <ESourceSignatureCapture
        id={inputId}
        value={value || ""}
        disabled={disabled}
        label={field.label || "Signature"}
        onChange={setVal}
      />
    );
  }

  if (field.type === "multi_select") {
    const selectedValues = Array.isArray(value) ? value.map(String) : [];
    const selected = choiceOptions.filter((o) =>
      selectedValues.includes(o.value),
    );
    return (
      <div
        id={inputId}
        className="esource-form-dd"
        role="group"
        aria-labelledby={`${inputId}-label`}
      >
        <Dropdown
          options={choiceOptions}
          isMulti
          showSearch
          showMultiSelectedTooltip
          disabled={disabled}
          selected={selected}
          onSelect={(opts) =>
            setVal(Array.isArray(opts) ? opts.map((o) => o.value) : [])
          }
        />
      </div>
    );
  }

  const inputType =
    field.type === "integer" || field.type === "number"
      ? "number"
      : field.type === "date"
        ? "date"
        : field.type === "datetime"
          ? "datetime-local"
          : field.type === "time"
            ? "time"
            : "text";

  const dateBounds =
    field.type === "date" || field.type === "datetime"
      ? dateInputBounds(field)
      : {};

  const step =
    field.type === "integer"
      ? field.step || "1"
      : field.type === "number"
        ? "any"
        : undefined;

  const digitsOnly =
    field.type === "text" && isDigitsOnlyPattern(field.pattern);
  const htmlPattern =
    field.type === "text" && String(field.pattern || "").trim()
      ? String(field.pattern).trim()
      : undefined;

  return (
    <div className="esource-form-input-wrap">
      <input
        id={inputId}
        className="form-control"
        type={inputType}
        inputMode={digitsOnly ? "numeric" : undefined}
        pattern={htmlPattern}
        disabled={disabled}
        value={value ?? ""}
        placeholder={field.placeholder || undefined}
        maxLength={
          field.type === "text" && field.maxLength
            ? Number(field.maxLength)
            : undefined
        }
        min={
          field.minValue !== "" && field.minValue != null
            ? field.minValue
            : dateBounds.min
        }
        max={
          field.maxValue !== "" && field.maxValue != null
            ? field.maxValue
            : dateBounds.max
        }
        step={step}
        onChange={(e) => {
          let next = e.target.value;
          if (digitsOnly) {
            next = next.replace(/\D/g, "");
            const max = field.maxLength ? Number(field.maxLength) : null;
            if (Number.isFinite(max) && max > 0) next = next.slice(0, max);
          }
          setVal(next);
        }}
      />
      {field.unit ? (
        <span className="esource-form-input-wrap__unit">{field.unit}</span>
      ) : null}
    </div>
  );
}

export function ESourceFieldBlock({
  field,
  value,
  disabled = false,
  onChange,
  idPrefix = "",
  className = "",
  invalid = false,
  allFields = [],
  allValues = {},
  forceFullWidth = false,
}) {
  const widthClass = forceFullWidth
    ? "col-12"
    : fieldWidthClass(field.width);
  const showLabel =
    !isLayoutField(field.type) &&
    field.type !== "checkbox" &&
    field.type !== "divider" &&
    field.type !== "spacer" &&
    field.type !== "pagebreak";
  const labelledByMulti =
    field.type === "radio" ||
    field.type === "yesno" ||
    field.type === "multi_select" ||
    field.type === "dropdown" ||
    field.type === "button_group";

  return (
    <div className={`${widthClass} ${className}`.trim()}>
      <div
        className={`esource-form-field mb-3${
          invalid ? " esource-form-field--invalid" : ""
        }`}
      >
        {showLabel ? (
          <label
            className="form-label esource-builder-field__label"
            htmlFor={`${idPrefix}${field.id}`}
            id={labelledByMulti ? `${idPrefix}${field.id}-label` : undefined}
          >
            {field.label}
            {field.required ? <span className="mandatory"> *</span> : null}
          </label>
        ) : null}
        <ESourceFieldControl
          field={field}
          value={value}
          disabled={disabled}
          onChange={onChange}
          idPrefix={idPrefix}
          allFields={allFields}
          allValues={allValues}
        />
        {invalid ? (
          <div className="esource-form-field__error">
            This field is required.
          </div>
        ) : null}
        {field.helpText &&
        !isLayoutField(field.type) &&
        field.type !== "repeating_table" ? (
          <div className="form-text">{field.helpText}</div>
        ) : null}
        {field.helpText && field.type === "repeating_table" ? (
          <div className="form-text">{field.helpText}</div>
        ) : null}
      </div>
    </div>
  );
}

export function ESourceFormFields({
  fields,
  values,
  disabled = false,
  onChange,
  idPrefix = "",
  emptyText = "This form has no fields.",
  invalidIds = [],
  logic = [],
  layout = "two-column",
}) {
  const invalidSet = new Set(invalidIds || []);
  const { visible, enabled } = useMemo(
    () => evaluateLogicVisibility(fields, values, logic),
    [fields, values, logic],
  );
  const forceFullWidth = layout === "one-column";

  if (!fields?.length) {
    return <p className="text-muted mb-0">{emptyText}</p>;
  }

  return (
    <div className="row g-2">
      {fields.map((field) => {
        if (visible[field.id] === false) return null;
        const fieldDisabled = disabled || enabled[field.id] === false;
        return (
          <ESourceFieldBlock
            key={field.id}
            field={field}
            value={values?.[field.id]}
            disabled={fieldDisabled}
            onChange={onChange}
            idPrefix={idPrefix}
            invalid={invalidSet.has(field.id)}
            allFields={fields}
            allValues={values}
            forceFullWidth={forceFullWidth}
          />
        );
      })}
    </div>
  );
}
